import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Fragment, ReactNode, useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { classNames } from "../../utils/cn";
import { withMarginTw } from "../../styles/withMargin";

export interface Option {
  label: string;
  description?: string;
  subtitle?: ReactNode;
  icon?: ReactNode;
  value: string | null;
}

const textSizeMap = {
  xs: "text-xs",
  s: "text-sm",
  m: "text-sm",
};

function RenderOption(props: {
  option: Option;
  variant?: "button" | "text" | "light";
  size?: "xs" | "s" | "m";
  hideSelectedOptionDescription?: boolean;
}) {
  return (
    <div className="flex items-center gap-x-2">
      {props.option.icon ? <>{props.option.icon}</> : null}
      <div>
        <p
          className={classNames(
            " truncate",
            props.variant === "light"
              ? "text-gray-500 font-normal"
              : "text-gray-600 font-semibold",
            textSizeMap[props.size ?? "m"],
          )}
        >
          {props.option.label}
        </p>
        {props.option.subtitle &&
        !props.hideSelectedOptionDescription &&
        (!props.variant || props.variant !== "text") ? (
          <div className="mt-0.5">{props.option.subtitle}</div>
        ) : props.option.description &&
          !props.hideSelectedOptionDescription &&
          (!props.variant || props.variant !== "text") ? (
          <p
            className={classNames(
              " text-gray-500 font-normal",
              textSizeMap[props.size ?? "m"],
            )}
          >
            {props.option.description}
          </p>
        ) : null}
      </div>
    </div>
  );
}

const paddingSizeMap = {
  xs: "py-1",
  s: "py-1.5",
  m: "py-2",
};

export default function Dropdown(props: {
  options: Option[];
  onSelect: (option: Option) => void;
  label?: string;
  defaultOption?: Option;
  selectedOption?: Option;
  margin?: string;
  variant?: "button" | "text" | "light";
  size?: "xs" | "s" | "m";
  hideSelectedOptionDescription?: boolean;
  renderOption?: (option?: Option) => ReactNode;
  searchable?: boolean;
}) {
  const margin = withMarginTw({ margin: props.margin });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const handleSelect = (option: Option) => {
    props.onSelect(option);
    setSearch("");
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
    } else if (props.searchable) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [isOpen, props.searchable]);

  const filteredOptions =
    props.searchable && search.trim()
      ? props.options.filter((o) =>
          o.label.toLowerCase().includes(search.toLowerCase()),
        )
      : props.options;

  const updateMenuPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: 0, // Let the menu expand to fit content
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updateMenuPosition();
      window.addEventListener("resize", updateMenuPosition);
      window.addEventListener("scroll", updateMenuPosition);

      return () => {
        window.removeEventListener("resize", updateMenuPosition);
        window.removeEventListener("scroll", updateMenuPosition);
      };
    }
  }, [isOpen]);

  return (
    <div className={` ${margin}`}>
      {props.label ? (
        <label className="block text-sm font-semibold leading-6 text-persian-990">
          {props.label}
        </label>
      ) : null}
      <Menu as="div" className="relative inline-block text-left w-full">
        {({ open }) => {
          // Update isOpen state when Menu state changes
          if (open !== isOpen) {
            setIsOpen(open);
          }

          return (
            <>
              <div>
                <Menu.Button
                  ref={buttonRef}
                  className={classNames(
                    "inline-flex w-full justify-between items-center text-left gap-x-1.5 rounded-md bg-white px-3  text-sm ",
                    props.variant && props.variant === "text"
                      ? ""
                      : props.variant === "light"
                        ? "shadow-sm ring-1 ring-inset font-light text-gray-500 ring-gray-300 outline-none focus:ring-gray-600 hover:bg-gray-50"
                        : "shadow-sm ring-2 ring-inset ring-gray-300 outline-none focus:ring-gray-600 hover:bg-gray-50 font-semibold text-gray-900",
                    paddingSizeMap[props.size ?? "m"],
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {props.renderOption ? (
                    props.renderOption(
                      props.selectedOption ?? props.defaultOption,
                    )
                  ) : (
                    <RenderOption
                      size={props.size}
                      variant={props.variant}
                      hideSelectedOptionDescription={
                        props.hideSelectedOptionDescription
                      }
                      option={
                        props.selectedOption ??
                        props.defaultOption ?? {
                          label: "Select",
                          value: "",
                        }
                      }
                    />
                  )}

                  <ChevronDownIcon
                    className="-mr-1 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </Menu.Button>
              </div>

              {isOpen &&
                createPortal(
                  <div
                    style={{
                      position: "absolute",
                      top: menuPosition.top,
                      left: menuPosition.left,
                      zIndex: 9999,
                    }}
                  >
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute left-0 z-10 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none min-w-max">
                        {props.searchable && (
                          <div className="bg-gray-50 px-3 py-2 border-b border-gray-100">
                            <input
                              ref={searchRef}
                              type="text"
                                className="flex-1 bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none ring-0 border-0 focus:ring-0 focus:outline-none"
                              placeholder="Search..."
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}
                        <div className="py-1 max-h-56 overflow-y-auto">
                          {filteredOptions.length === 0 ? (
                            <p className="px-4 py-2 text-sm text-gray-400">
                              No results
                            </p>
                          ) : (
                            filteredOptions.map((o) => (
                              <Menu.Item key={o.value}>
                                {({ active }) => (
                                  <button
                                    type="button"
                                    className={classNames(
                                      active
                                        ? "bg-gray-100 text-gray-900"
                                        : "text-gray-700",
                                      "block px-4 py-2 text-sm w-full text-left",
                                    )}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelect(o);
                                    }}
                                  >
                                    <RenderOption option={o} />
                                  </button>
                                )}
                              </Menu.Item>
                            ))
                          )}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </div>,
                  document.body,
                )}
            </>
          );
        }}
      </Menu>
    </div>
  );
}
